// Transcription State Manager
// Manages the state of ongoing transcriptions across component lifecycles

interface TranscriptionState {
    videoId: number;
    clientId: string;
    segments: any[];
    isProcessing: boolean;
    eventSource?: EventSource;
    lastUpdate?: Date;
}

class TranscriptionStateManager {
    private states: Map<string, TranscriptionState> = new Map();

    private getKey(clientId: string, videoId: number): string {
        return `${clientId}-${videoId}`;
    }

    // Save or update transcription state
    setState(clientId: string, videoId: number, state: Partial<TranscriptionState>) {
        const key = this.getKey(clientId, videoId);
        const existing = this.states.get(key) || { videoId, clientId, segments: [], isProcessing: false };
        
        this.states.set(key, {
            ...existing,
            ...state,
            lastUpdate: new Date()
        });
    }

    // Get transcription state
    getState(clientId: string, videoId: number): TranscriptionState | null {
        const key = this.getKey(clientId, videoId);
        return this.states.get(key) || null;
    }

    // Check if transcription is in progress
    isProcessing(clientId: string, videoId: number): boolean {
        const state = this.getState(clientId, videoId);
        return state?.isProcessing || false;
    }

    // Get segments
    getSegments(clientId: string, videoId: number): any[] {
        const state = this.getState(clientId, videoId);
        return state?.segments || [];
    }

    // Clean up old states (older than 30 minutes)
    cleanup() {
        const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
        
        this.states.forEach((state, key) => {
            if (state.lastUpdate && state.lastUpdate < thirtyMinutesAgo && !state.isProcessing) {
                // Close any lingering EventSource
                if (state.eventSource) {
                    state.eventSource.close();
                }
                this.states.delete(key);
            }
        });
    }

    // Clear specific state
    clearState(clientId: string, videoId: number) {
        const key = this.getKey(clientId, videoId);
        const state = this.states.get(key);
        
        if (state?.eventSource) {
            state.eventSource.close();
        }
        
        this.states.delete(key);
    }
}

// Create singleton instance
export const transcriptionStateManager = new TranscriptionStateManager();

// Run cleanup every 5 minutes
setInterval(() => {
    transcriptionStateManager.cleanup();
}, 5 * 60 * 1000);